import { morphSection } from '@theme/section-renderer';
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/**
 * Pillar gift guard (see snippets/st-pillar-gift-guard.liquid, which computes what the cart earns).
 *
 * 1. LOWER - gift lines above what the Pillars earn (data-fix) are set down in ONE /cart/update.js call.
 * 2. TOP UP (v2 26/09/2026) - when the cart earns MORE of a gift than at the last check (a Pillar added
 *    from a collection card has no gifts; 1 -> 2 Pillars with the stepper earns the 8-oil add-on), the
 *    missing gifts are added. A gift the shopper removed while the cart kept earning it is not forced
 *    back - the Lavender rule the owner set on 14/09. What was earned is remembered per cart
 *    (sessionStorage + cart token), so a new cart after an order starts from nothing.
 * 3. CHECKOUT GATE (v2) - the Check out button posts the cart form, which carries every quantity box as
 *    updates[]: typing 3 in the 6-oil-set box and pressing Check out reached checkout with three free sets
 *    before any check ran (reproduced on live 26/09). The submit is now held until every cart request has
 *    settled and the drawer's guard data, freshly rendered by the server, has been applied; it then goes
 *    out WITHOUT the updates[] boxes. Wallet buttons on /cart (iframes) are inert while a check is due.
 *
 * One request chain at a time; pauses after 2 failures in a row (20 s) and stops re-sending a fix the cart
 * ignored three times, so it cannot loop. A cart link (/cart/<id>:1) skips the storefront - the Flow order
 * check covers that.
 */
const STATE_KEY = 'st-pillar-gift:v3';
const SECTION = 'cart-drawer-section';

// state shared by the guard and the checkout gate (declared first: defining the element upgrades it at once)
const gate = { busy: false, again: false, inflight: 0, lastMutation: 0, lastQuantity: 0 };
const pending = () => gate.busy || gate.inflight > 0 || Date.now() - gate.lastQuantity < 450;
const paint = () => document.documentElement.toggleAttribute('data-st-gift-pending', pending());

const cartToken = () => {
  const c = document.cookie.split('; ').find((x) => x.startsWith('cart='));
  return c ? decodeURIComponent(c.slice(5)) : '';
};
// memory per cart, per gift: e = earned, h = kept, p = {variant id of the Pillar line holding it: qty}
const readPrev = () => {
  try {
    const s = JSON.parse(sessionStorage.getItem(STATE_KEY) || 'null');
    return s && s.token === cartToken() ? s.pairs || {} : {};
  } catch {
    return {};
  }
};
const writeState = (pairs) => {
  try {
    sessionStorage.setItem(STATE_KEY, JSON.stringify({ token: cartToken(), pairs }));
  } catch {}
};

/**
 * the rules printed by the snippet: ladder (pg_rules) = gift handle -> Pillars needed; tiers (pg_tiers) = the offer's
 * tier sizes (Buy 1 / 2 / 3); perOrder (pg_per_order) = gifts given once per order (the eBooks)
 */
function rulesOf(el) {
  const ladder = {};
  for (const row of (el.dataset.ladder || '').split(',').filter(Boolean)) {
    const [handle, from] = row.split(':');
    ladder[handle] = Number(from) || 1;
  }
  let variants = {};
  try {
    variants = JSON.parse(el.dataset.variants || '{}');
  } catch {}
  const tiers = (el.dataset.tiers || '').split(',').map(Number).filter((q) => q >= 1);
  // a page rendered before 30/09 has no data-per-order: the eBooks are the per-order gift (owner 30/09)
  const perOrder = (el.dataset.perOrder ?? 'ritual-collection-ebooks-free-gift').split(',').filter(Boolean);
  // rule #2 (30/09): gift handle -> variant of its real, sellable product (only the CalmRide gift has one)
  let twins = {};
  try {
    twins = JSON.parse(el.dataset.twins || '{}');
  } catch {}
  return { main: el.dataset.main || 'waterless-cold-air-diffuser-large-room', ladder, variants, tiers, perOrder, twins };
}

// <combo> 30/09/2026 combo scaling (owner: "đồng ý và ebook chỉ cần tặng 1"; skill gift-rules.md rule #7, the same
// rule Titanium Wares went live with today). The Pillars are split into the offer's tiers, biggest first, again and
// again, and the gift sets of the pieces add up: 4 = Buy 3 + Buy 1, 5 = Buy 3 + Buy 2, 6 = Buy 3 + Buy 3 (before
// 30/09: one of each gift at most). A tier's set = every gift whose ladder step it reaches (Buy 1 = eBooks +
// pillowcase + 6-oil set, Buy 2 adds the 8-oil upgrade, Buy 3 adds CalmRide). perOrder gifts stay at 1 (the eBooks).
// Units left below the smallest tier earn nothing. Rules without tiers (a page cached during the rollout) fall back
// to the ladder's own steps. The Flow order check (cold-air-s150/gift-flow/build-flow-code.js) pastes this block.
function comboGifts(R, units) {
  const qtys = [...new Set(R.tiers && R.tiers.length ? R.tiers : Object.values(R.ladder))].filter((q) => q >= 1).sort((a, b) => b - a);
  const out = {};
  let left = Math.max(0, Math.floor(Number(units) || 0));
  for (const q of qtys) {
    const times = Math.floor(left / q);
    if (!times) continue;
    for (const [gift, from] of Object.entries(R.ladder)) if (from <= q) out[gift] = (out[gift] || 0) + times;
    left -= times * q;
  }
  for (const gift of R.perOrder || []) if (out[gift] > 1) out[gift] = 1;
  return out;
}
// </combo>

/**
 * v3 29/09/2026 (bundle standard, "logic bên kaching, kết hợp chung với của mình"): the gifts are CHILD lines of
 * a Pillar line (Shopify nested cart lines, the Kaching way) - remove the Pillar and Shopify removes its gifts,
 * and the drawer / checkout list them under it. Planned from the cart JSON (not from the section attributes),
 * so the morph order of data-fix / data-earned no longer matters.
 * Shopify facts (measured 29/09): a child sent with parent_id = a variant in the SAME request hangs on that
 * line; an existing line only takes children by parent_line_key; line keys change after most cart edits, so
 * the memory is kept per gift by the VARIANT of the holding line; children never follow the parent's quantity.
 *  - LOWER: a gift above what the Pillars earn; any gift line under no Pillar (old cart, cart link) is taken off
 *  - ADD: (a) such a loose gift the cart earns, re-hung under a Pillar; (b) what the cart earns MORE of since the
 *    last check (quick add, stepper); (c) gifts that vanished WITH their Pillar line (that colour removed or
 *    swapped) while other Pillars still earn them. A gift the shopper removed while its line stayed is not
 *    forced back (the Lavender rule the owner set on 14/09).
 */
async function planCart(el) {
  const cart = await (await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } })).json();
  return planFrom(el, cart);
}

/** the plan for a given cart (pure: also used by assets/st-fast-cart.js on the cart as it WILL be) */
function planFrom(el, cart) {
  const R = rulesOf(el);
  const items = cart.items || [];
  const byKey = new Map(items.map((i) => [i.key, i]));
  const mains = items.filter((i) => i.handle === R.main && !i.parent_relationship);
  const units = mains.reduce((s, i) => s + i.quantity, 0);
  const anchor = mains.reduce((a, i) => (!a || i.quantity > a.quantity ? i : a), null);
  const mainVariants = new Set(mains.map((i) => i.variant_id));

  const updates = {};
  const linesOf = {};
  const orphan = {};
  for (const item of items) {
    if (!(item.handle in R.ladder)) continue;
    const parent = byKey.get(item.parent_relationship?.parent_key || '');
    if (parent && parent.handle === R.main) (linesOf[item.handle] = linesOf[item.handle] || []).push(item);
    else {
      orphan[item.handle] = (orphan[item.handle] || 0) + item.quantity;
      updates[item.key] = 0;
    }
  }

  const before = readPrev();
  const adds = [];
  const paid = [];
  const next = {};
  const earned = comboGifts(R, units);
  for (const gift of Object.keys(R.ladder)) {
    // what the Pillars earn of this gift (combo, 30/09/2026); only a RISE of it is added, so a gift the shopper
    // deleted stays deleted when the quantity goes up - just the newly earned units come
    const n = earned[gift] || 0;
    let got = 0;
    let held = 0;
    const p = {};
    let left = n;
    for (const line of linesOf[gift] || []) {
      held += line.quantity;
      const keep = Math.min(line.quantity, left);
      left -= keep;
      if (keep !== line.quantity) updates[line.key] = keep;
      if (keep > 0) {
        got += keep;
        const v = byKey.get(line.parent_relationship.parent_key).variant_id;
        p[v] = (p[v] || 0) + keep;
      }
    }
    const missing = n - got;
    const b = before[gift] || {};
    // rule #2 (30/09/2026): the shopper RAISED a gift above what the Pillars earn (typed a quantity, cart edit) -> the
    // gift goes back to the earned count and the extra units become a normal paid line of the real product. Gifts
    // above a LOWER entitlement (fewer Pillars) are only trimmed (rule #5), and so are gifts with no sellable twin.
    if (held > got && R.twins[gift] && b.h !== undefined) {
      const convert = Math.min(held - got, Math.max(0, held - (Number(b.h) || 0)));
      if (convert > 0) paid.push({ id: Number(R.twins[gift]), quantity: convert, pair: gift });
    }
    let add = 0;
    if (missing > 0 && anchor && R.variants[gift]) {
      const adopt = Math.min(missing, orphan[gift] || 0);
      const rise = n - (Number(b.e) || 0);
      let lost = 0;
      for (const [v, q] of Object.entries(b.p || {})) if (!mainVariants.has(Number(v))) lost += Number(q) || 0;
      add = Math.min(missing, adopt + Math.max(rise, Math.min(lost, (Number(b.h) || 0) - got), 0));
      if (add > 0) {
        adds.push({ id: Number(R.variants[gift]), quantity: add, parentVariant: anchor.variant_id, pair: gift });
        p[anchor.variant_id] = (p[anchor.variant_id] || 0) + add;
      }
    }
    if (n || got || add) next[gift] = { e: n, h: got + add, p };
  }
  return { updates, adds, paid, next, cart };
}
const isEmpty = (p) => !Object.keys(p.updates).length && !p.adds.length && !(p.paid || []).length;

// for the fast + / − path (assets/st-fast-cart.js): plan a cart, remember a plan, the rules element, the real
// product of a gift (rule #2)
window.__stPillar = {
  planFrom,
  writeState,
  el: () => document.querySelector('st-pillar-gift-guard'),
  twinOf: (handle) => {
    const el = document.querySelector('st-pillar-gift-guard');
    return el ? Number(rulesOf(el).twins[handle]) || 0 : 0;
  },
};

const post = (url, body) =>
  fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
const postJson = async (url, body) => {
  const response = await post(url, body);
  const data = await response.json();
  if (!response.ok || data?.status) throw new Error(data?.description || data?.message || 'Pillar gift guard failed');
  return data;
};

let failures = 0;
let lastKey = '';
let lastAt = 0;
let repeats = 0;

const drainAgain = () => {
  // a guard element that changed while we were busy skipped its run: check it now (29/09/2026)
  if (!gate.again) return;
  gate.again = false;
  for (const el of document.querySelectorAll('st-pillar-gift-guard')) el.recheck?.();
};

/** apply a plan: lower first (its answer has fresh line keys), hang the adds on a Pillar line, repaint */
async function apply(p, sectionId, mode, force = false) {
  const key = JSON.stringify([p.updates, p.adds, p.paid || []]);
  if (gate.busy) return;
  if (failures && Date.now() - lastAt > 20000) failures = 0; // a network blip must not switch the guard off for good
  // the very same fix three times in a row within 5 s means the cart is not taking it: stop, never loop
  repeats = key === lastKey && Date.now() - lastAt < 5000 ? repeats + 1 : 0;
  if (!force && (failures >= 2 || repeats >= 3)) return;
  gate.busy = true;
  lastKey = key;
  lastAt = Date.now();
  paint();
  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(new CartLinesUpdateEvent({ action: 'update', context: 'cart', lines: [], promise: deferred.promise }));
  const sections = sectionId ? { sections: [sectionId], sections_url: window.location.pathname } : {};
  try {
    let data = p.cart;
    const paid = p.paid || [];
    const more = p.adds.length || paid.length;
    if (Object.keys(p.updates).length) data = await postJson(Theme.routes.cart_update_url, { updates: p.updates, ...(more ? {} : sections) });
    if (more) {
      const keyOf = (variant) => (data.items || []).find((i) => i.variant_id === variant && !i.parent_relationship)?.key;
      const items = p.adds.map((a) => ({ id: a.id, quantity: a.quantity, parent_line_key: keyOf(a.parentVariant) })).filter((i) => i.parent_line_key);
      // rule #2: the extra units as the real product, a normal top-level paid line
      for (const x of paid) items.push({ id: x.id, quantity: x.quantity });
      if (items.length) {
        const added = await post(Theme.routes.cart_add_url, { items });
        // /cart/add.js is all-or-nothing: one gift that went unavailable must not hold back the others
        if (!added.ok && items.length > 1) for (const item of items) await post(Theme.routes.cart_add_url, { items: [item] });
      }
      data = await postJson(Theme.routes.cart_update_url, { updates: {}, ...sections });
    }
    writeState(p.next);
    failures = 0;
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(data),
      ...(data.sections ? { detail: { sections: data.sections } } : {}),
    });
    const html = sectionId ? data.sections?.[sectionId] : null;
    if (html) morphSection(sectionId, html, { mode });
  } catch (error) {
    deferred.reject(error);
    failures++;
    console.warn('[st-pillar-gift-guard]', error);
  } finally {
    gate.busy = false;
    paint();
    drainAgain();
  }
}

class PillarGiftGuard extends HTMLElement {
  static observedAttributes = ['data-fix', 'data-earned'];

  #queued = false;

  connectedCallback() {
    this.#schedule();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue !== newValue && this.isConnected) this.#schedule();
  }

  recheck() {
    this.#schedule();
  }

  // a section morph sets several attributes one after the other: run once, one microtask later
  #schedule() {
    if (this.#queued) return;
    this.#queued = true;
    queueMicrotask(() => {
      this.#queued = false;
      if (this.isConnected) this.#run();
    });
  }

  async #run() {
    if (gate.busy) {
      gate.again = true;
      return;
    }
    gate.busy = true;
    paint();
    let p = null;
    try {
      p = await planCart(this);
    } catch (error) {
      console.warn('[st-pillar-gift-guard]', error);
    } finally {
      gate.busy = false;
      paint();
    }
    if (!p) return drainAgain();
    if (isEmpty(p)) {
      writeState(p.next);
      return drainAgain();
    }
    apply(p, this.dataset.sectionId || '', this.closest('theme-drawer') ? 'hydration' : 'full');
  }
}

if (!customElements.get('st-pillar-gift-guard')) customElements.define('st-pillar-gift-guard', PillarGiftGuard);

// An empty cart renders no guard element (the drawer footer and the /cart summary only exist with items), so
// "earned" would keep the last non-empty value and the next Pillar would not get its gifts: forget it whenever
// the cart becomes empty, and on a page that loads with no guard element (= an empty cart).
if (!document.querySelector('st-pillar-gift-guard')) writeState({});
// the v2 memory (per gift handle, no holders) is not read any more
try {
  sessionStorage.removeItem('st-pillar-gift:earned');
} catch {}
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const promise = event.promise ?? event.detail?.promise;
  promise
    ?.then((result) => {
      if (result?.cart && Number(result.cart.totalQuantity ?? result.cart.item_count ?? 1) === 0) writeState({});
    })
    .catch(() => {});
});

// ---------------------------------------------------------------------------------------------------------
// Checkout gate + "a check is due" state for the wallet buttons
// ---------------------------------------------------------------------------------------------------------
if (!window.__stPillarGate) {
  window.__stPillarGate = true;
  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  const style = document.createElement('style');
  style.textContent =
    'html[data-st-gift-pending] .additional-checkout-buttons,html[data-st-gift-pending] shopify-accelerated-checkout-cart{pointer-events:none;opacity:.55;transition:opacity .15s}';
  document.head.appendChild(style);

  // every cart mutation on the wire, whoever sent it (theme, apps, this file)
  const MUTATION = /\/cart\/(add|change|update|clear)(\.js)?$/;
  const nativeFetch = window.fetch;
  window.fetch = function (input, init) {
    const promise = nativeFetch.apply(this, arguments);
    try {
      const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input?.url || '';
      const method = String(init?.method || (input instanceof Request ? input.method : 'GET')).toUpperCase();
      if (method !== 'GET' && MUTATION.test(new URL(url, location.href).pathname)) {
        gate.inflight++;
        gate.lastMutation = Date.now();
        paint();
        const done = () => {
          gate.inflight--;
          gate.lastMutation = Date.now();
          paint();
        };
        promise.then(done, done);
      }
    } catch {}
    return promise;
  };

  document.addEventListener('quantity-selector:update', () => {
    gate.lastQuantity = Date.now();
    paint();
    setTimeout(paint, 500);
  });

  /** the server's own view of the cart guard, rendered fresh, then applied */
  const freshCheck = async () => {
    const root = window.Shopify?.routes?.root || '/';
    const json = await (await fetch(`${root}?sections=${SECTION}`, { headers: { Accept: 'application/json' } })).json();
    const doc = new DOMParser().parseFromString(json?.[SECTION] || '', 'text/html');
    const el = doc.querySelector('st-pillar-gift-guard');
    if (!el) return;
    const p = await planCart(el);
    if (isEmpty(p)) return writeState(p.next);
    await apply(p, document.getElementById(`shopify-section-${SECTION}`) ? SECTION : '', 'hydration', true);
  };

  let released = false;
  let holding = false;
  const neutralise = (form) => {
    for (const el of form.elements) if (el.name === 'updates[]') el.disabled = true;
  };
  // a quantity typed into a box and not sent yet (Check out pressed straight away): send it first, through
  // the cart API, so the check below sees it - the box itself is never posted
  const pushTyped = async (form) => {
    const updates = {};
    for (const el of form.elements) {
      if (el.name !== 'updates[]' || el.disabled) continue;
      const key = el.closest('[data-key]')?.dataset.key;
      const now = el.value === '' ? NaN : Number(el.value);
      if (key && Number.isInteger(now) && now >= 0 && now !== Number(el.defaultValue)) updates[key] = now;
    }
    if (Object.keys(updates).length) await post(Theme.routes.cart_update_url, { updates });
  };
  window.addEventListener(
    'submit',
    async (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      const submitter = event.submitter;
      if (submitter ? submitter.name !== 'checkout' : form.id !== 'cart-form') return;
      if (released) {
        released = false;
        neutralise(form);
        return;
      }
      event.preventDefault();
      event.stopImmediatePropagation();
      if (holding) return; // a second click while the first one is being checked
      holding = true;
      submitter?.setAttribute('aria-busy', 'true');

      const until = Date.now() + 6000;
      const quiet = () => !gate.busy && gate.inflight === 0 && Date.now() - gate.lastMutation > 350 && Date.now() - gate.lastQuantity > 450;
      const hold = async () => {
        await sleep(60); // a typed quantity dispatches its change on blur, just before this submit
        while (!quiet() && Date.now() < until) await sleep(50);
        await pushTyped(form);
        while (!quiet() && Date.now() < until) await sleep(50);
        await freshCheck();
        while (!quiet() && Date.now() < until) await sleep(50);
      };
      // never keep a buyer from checkout: a slow network lets the order through after 8 s
      await Promise.race([hold().catch(() => {}), sleep(8000)]);

      const liveForm = (form.isConnected ? form : document.getElementById(form.id)) || form;
      const button = [...document.querySelectorAll('[name="checkout"]')].find((b) => b.form === liveForm);
      neutralise(liveForm); // disabled boxes are left out of the post AND out of form validation
      released = true;
      try {
        if (button && liveForm.requestSubmit) liveForm.requestSubmit(button);
        else throw new Error('no requestSubmit');
      } catch {
        const flag = document.createElement('input');
        flag.type = 'hidden';
        flag.name = 'checkout';
        flag.value = '1';
        liveForm.appendChild(flag);
        liveForm.submit();
      }
      released = false;
      holding = false;
      submitter?.removeAttribute('aria-busy');
    },
    true
  );
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    for (const el of document.querySelectorAll('[name="updates[]"]')) el.disabled = false;
  });
}
